import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, Check } from 'lucide-react';

const CustomSelect = ({ value, onChange, options, placeholder = 'Seleccionar...', className = '', size = 'md' }) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const btnRef = useRef(null);
  const menuRef = useRef(null);
  const searchRef = useRef(null);

  const selected = options.find(o => o.value === value);
  const showSearch = options.length > 6;

  const filtered = search
    ? options.filter(o => o.label.toLowerCase().includes(search.toLowerCase()))
    : options;

  useEffect(() => {
    if (!open) { setSearch(''); return; }
    const handle = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target) && !btnRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    const handleEsc = (e) => { if (e.key === 'Escape') setOpen(false); };
    setTimeout(() => {
      document.addEventListener('mousedown', handle);
      document.addEventListener('keydown', handleEsc);
    }, 10);
    if (showSearch) setTimeout(() => searchRef.current?.focus(), 50);
    return () => {
      document.removeEventListener('mousedown', handle);
      document.removeEventListener('keydown', handleEsc);
    };
  }, [open]);

  const sizeClasses = {
    sm: 'text-[11px] px-2 py-1 gap-1',
    md: 'text-[13px] px-3 py-2 gap-1.5',
  };

  const rect = btnRef.current?.getBoundingClientRect();
  const menuStyle = rect ? {
    position: 'fixed',
    top: rect.bottom + 4,
    left: rect.left,
    width: Math.max(rect.width, 180),
    zIndex: 99999,
  } : {};

  // Flip up if near bottom
  if (rect && rect.bottom + 260 > window.innerHeight) {
    menuStyle.top = rect.top - 4;
    menuStyle.transform = 'translateY(-100%)';
  }

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        onClick={() => setOpen(!open)}
        className={`flex items-center justify-between border border-gray-200 rounded-lg bg-white hover:border-gray-300 focus:border-blue-400 focus:ring-2 focus:ring-blue-100 transition-all outline-none ${sizeClasses[size]} ${className}`}
      >
        <span className={`truncate ${selected ? 'text-gray-800' : 'text-gray-400'}`}>
          {selected ? selected.label : placeholder}
        </span>
        <ChevronDown size={size === 'sm' ? 12 : 14} className={`text-gray-400 shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && createPortal(
        <div ref={menuRef} style={menuStyle}
          className="bg-white rounded-xl shadow-2xl border border-gray-200 overflow-hidden animate-in fade-in zoom-in-95 duration-100">
          {showSearch && (
            <div className="p-2 border-b border-gray-100">
              <input
                ref={searchRef}
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Buscar..."
                className="w-full text-[12px] px-2.5 py-1.5 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:border-blue-400 focus:bg-white transition-colors"
              />
            </div>
          )}
          <div className="max-h-[220px] overflow-y-auto py-1 scroll-smooth" style={{ scrollbarWidth: 'thin' }}>
            {filtered.length === 0 ? (
              <div className="px-3 py-4 text-center text-xs text-gray-400">Sin resultados</div>
            ) : filtered.map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => { onChange(option.value); setOpen(false); }}
                className={`w-full flex items-center gap-2 px-3 py-2 text-[13px] transition-colors text-left ${
                  option.value === value
                    ? 'bg-blue-50 text-blue-700 font-medium'
                    : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                {option.icon && <span className="shrink-0">{option.icon}</span>}
                <span className="truncate flex-1">{option.label}</span>
                {option.value === value && <Check size={14} className="text-blue-600 shrink-0" />}
              </button>
            ))}
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

export default CustomSelect;
